/**
 * 파트너 화면 모달 폼 공용 — 매장 소식(initPartnerNews.js) · 매장 케어(initPartnerGuide.js).
 *
 * admin-ajax 로 보내고, footer 안내(.notice)를 띄우고, 저장이 끝나면 목록을 서버가 다시 그리도록
 * 새로고침한다. 새로고침 뒤에 띄울 알림은 sessionStorage 에 잠깐 맡긴다.
 */

/**
 * admin-ajax 로 보낸다. 서버는 오류도 HTTP 200 + success:false 로 답한다.
 * 사진(data URL)처럼 큰 값은 URL 인코딩하면 30% 가까이 불어나므로 FormData 로 보낸다.
 *
 * @param {HTMLElement} root data-ajax-url · data-nonce 를 가진 요소(폼)
 * @param {string} action admin-ajax 액션
 * @param {Record<string, string | number> | FormData} fields 값 묶음. 파일이 든 폼은
 *   FormData(form) 을 그대로 준다 — action · nonce 는 여기서 덧붙인다.
 * @return {Promise<Record<string, any>>}
 */
export const postForm = async (root, action, fields) => {
  const payload = fields instanceof FormData ? fields : new FormData();

  payload.set('action', action);
  payload.set('nonce', root.dataset.nonce || '');

  if (!(fields instanceof FormData)) {
    Object.entries(fields).forEach(([key, value]) => payload.append(key, String(value)));
  }

  let response;

  try {
    response = await fetch(root.dataset.ajaxUrl || '', {
      method: 'POST',
      body: payload,
      credentials: 'same-origin',
    });
  } catch (error) {
    throw new Error('인터넷 연결을 확인한 뒤 다시 시도해 주세요.');
  }

  const json = await response.json().catch(() => null);

  if (!json || !json.success) {
    // 통합 관리 계정이 매장을 다시 골라야 한다(목록에서 빠졌거나 아직 안 골랐다) — 선택 화면으로
    if (json?.data?.reselect && json.data.redirect) {
      window.location.assign(json.data.redirect);
    }

    throw new Error(
      json?.data?.message ||
        `처리하지 못했습니다. 잠시 후 다시 시도해 주세요. (HTTP ${response.status})`
    );
  }

  return json.data || {};
};

/** 모달 footer 의 안내(.notice) — 빈 문구면 감춘다 */
export const setNotice = (status, message, state = 'error') => {
  const text = status?.querySelector('.notice__text');

  if (!status || !text) {
    return;
  }

  status.classList.remove('notice--error', 'notice--warning', 'notice--success');

  if ('' === message) {
    status.hidden = true;
    return;
  }

  status.classList.add(`notice--${state}`);
  text.textContent = message;
  status.hidden = false;
};

/** 화면 아래에 잠깐 띄우는 알림 (공통 .toast + .notice) */
export const showToast = (message, state = 'success') => {
  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.setAttribute('role', 'status');
  toast.innerHTML =
    `<p class="notice notice--${state}">` +
    '<span class="notice__icon" aria-hidden="true"></span>' +
    '<span class="notice__text"></span>' +
    '</p>';
  toast.querySelector('.notice__text').textContent = message;

  document.body.append(toast);
  window.setTimeout(() => toast.remove(), 4000);
};

/**
 * 알림을 맡기고 목록을 다시 그리게 한다. url 을 주면 그 주소로 옮긴다.
 *
 * @param {string} key 화면마다 다른 sessionStorage 키
 * @param {string} message
 * @param {string} url
 */
export const reloadWith = (key, message, url = '') => {
  try {
    window.sessionStorage.setItem(key, message);
  } catch (error) {
    // 저장소를 못 쓰는 브라우저면 알림만 빠진다
  }

  if (url && url !== window.location.href) {
    window.location.assign(url);
  } else {
    window.location.reload();
  }
};

/** 새로고침 전에 맡겨 둔 알림이 있으면 띄운다 */
export const showFlash = (key) => {
  try {
    const message = window.sessionStorage.getItem(key);

    if (message) {
      window.sessionStorage.removeItem(key);
      showToast(message);
    }
  } catch (error) {
    // 저장소를 못 쓰는 브라우저 — 띄울 것도 없다
  }
};
